import { UniqueID } from "@modules/elements/types/unique-id";
import { RecordsService } from "../services/records.service";

// Types
export type RecordStartTrigger = 'manual' | 'startTime' | 'scteTrigger' | 'timecode';
export type RecordDuration = 'seconds' | 'frames' | 'timecode';
export type RecordStatus = 'scheduled' | 'preparing' | 'inprogress' | 'finished' | 'canceled' | 'error';
export type RecordDeliverableFormat = 'j2kp' | 'j2ki' | 'xdcam' | 'dnx' | 'h264' | 'ts' | 'no_video_only_cc';
export type RecordDeliverableDestination = 'vod' | 'max' | 'maxChimera' | 'archive' | 'c3' | 'logs' | 'backup' | 'cinedeck' | 'linear' | 'contentAcquisition';
export type RecordTaskType =
  'updateCinedeckSettings' |
  'recording' |
  'vodTrim' | 'vodTranscode' | 'vodMetadata' | 'vodUpload' |
  'contentAcquisitionTrim' | 'contentAcquisitionTranscode' | 'contentAcquisitionMetadata' | 'contentAcquisitionUpload' |
  'maxParityTrim' | 'maxParityTranscode' | 'maxParityMetadata' | 'maxParityUpload' | 'maxParityOvertimeTrim' | 'maxParityOvertimeMetadata' |
  'maxTrim' | 'maxTranscode' | 'maxMetadata' | 'maxUpload' |
  'linearTrim' | 'linearTranscode' | 'linearFfmpegTranscode' | 'linearFfmpegMetadata' | 'linearMetadata' | 'linearUpload' |
  'archiveMetadata' | 'archiveOvertimeMetadata' | 'archiveUpload' | 'archiveOvertimeUpload' |
  'c3Upload' | 'c3ExternalOutput' | 'sourceRouter' |
  'restripe' |
  'backupUpload' |
  's3downloadVideo' | 'loadVideoToPlayback' | 'insertCaptions' |
  'startManualTask' | 'vodStartManualUpload' | 'maxParityStartManualUpload' | 'maxStartManualUpload' | 'linearStartManualUpload' | 'contentAcquisitionStartManualUpload' | 'irisStartManualUpload' | 'irisOvertimeStartManualUpload' |
  'irisUpload' | 'vodIrisUpload' | 'maxParityIrisUpload' | 'maxIrisUpload' | 'linearIrisUpload' |
  'icapStartManual' | 'startIcap' | 'setupIcap' | 'insertCaptions' |
  'validateFiles' |
  'collectS3Links' |
  'finished';
export type RecordTaskStatus = 'waiting' | 'inProgress' | 'done';
// Task body `behavior`: 'once' means the pipeline will not start the task again once it is done.
export type RecordTaskBehavior = 'multiple' | 'once';
// Old
// export type RecordInterval = 'once' | 'repeat';


export class RecordTask {
  id?: string;
  key!: string;
  type!: RecordTaskType;
  status?: RecordTaskStatus;
  timeStart?: string;
  timeEnd?: string;
  name?: string;
  fileName?: string;
  group?: string;
  needs!: RecordTaskType[];
  body?: any;
  progress?: number | null; // 0-100
}

export interface RecordTaskProgress {
  id: string;              // recordId
  serverId: string;
  taskKey: string;
  percent: number;
  remainingTime?: number;  // seconds
  processed?: number;      // in `unit`
  total?: number;          // in `unit`
  unit?: string;           // e.g. 'bytes'
}

export class RecordServer {
  id!: string;
  name?: string;
  version?: string;
  ip?: string;
  isServerOnline?: boolean;
  isCinedeckOnline!: boolean;
  recordChannel!: number;
  playbackChannel!: number;
  type!: string; // primary, backup
  instance?: RecordServerInstance;
  channels?: RecordServerChannel[];
  tasks!: RecordTask[];
  settings?: any;
  sortOrder?: number;
}

export class RecordServerInstance {
  instanceId?: string;
  privateIp?: string;
  status?: string;
}

export class RecordServerChannel {
  channel?: number;
  encoder?: number;
  filename?: string;
  format?: string;
}

export class Record {
  id?: string;                // Unique identifier of the record
  status?: RecordStatus;      // Status of record
  title?: string;             // Title of the record
  reference?: string;         // Arbitrary identifier proided by the user
  startTime?: string;         // Timestamp of record start in Seconds in UTC
  durationType?: RecordDuration;
  durationValue?: string;
  additionalMetadata?: {[key: string]: any};
  projectId?: string;
  servers?: RecordServer[];

  constructor() {
    this.status = 'scheduled';
    this.durationType = "seconds";
    this.additionalMetadata = {};
  }

  static sortServers(servers?: RecordServer[]): RecordServer[] {
    // const pattern: any[] = ['aws', 'selfHosted'];
    return servers
      // ?.sort((a, b) => pattern.indexOf(a.type) === pattern.indexOf(b.type) ? 0 : (pattern.indexOf(a.type) > pattern.indexOf(b.type) ? 1 : -1))
      ?.sort((a, b) => (a?.sortOrder || 0) - (b?.sortOrder || 0))
      ?.sort((a, b) => Number(b.name === "Primary") - Number(a.name === "Primary"))
       || [];
  }

  static sortTasks(tasks?: RecordTask[]): RecordTask[] {
    const pattern: any[] = [
      'updateCinedeckSettings', 'c3ExternalOutput', 'sourceRouter',
      's3downloadVideo', 'loadVideoToPlayback',
      'icapStartManual', 'startIcap', 'setupIcap',
      'recording',
      'vodTrim', 'vodTranscode', 'vodInsertCaptions', 'vodMetadata', 'vodValidateFiles', 'vodIrisUpload', 'vodStartManualUpload', 'vodUpload',
      'maxParityTrim', 'maxParityTranscode', 'maxParityMetadata', 'maxParityValidateFiles', 'maxParityIrisUpload', 'maxParityStartManualUpload', 'maxParityUpload',
      'maxTrim', 'maxTranscode', 'maxMetadata', 'maxValidateFiles', 'maxIrisUpload', 'maxStartManualUpload', 'maxUpload',
      'linearTrim', 'linearTranscode', 'linearFfmpegTranscode', 'linearFfmpegMetadata', 'linearInsertCaptions', 'linearMetadata', 'linearValidateFiles', 'linearIrisUpload', 'linearStartManualUpload', 'linearUpload',
      'contentAcquisitionTrim', 'contentAcquisitionTranscode', 'contentAcquisitionInsertCaptions', 'contentAcquisitionMetadata', 'contentAcquisitionValidateFiles', 'contentAcquisitionIrisUpload', 'contentAcquisitionStartManualUpload', 'contentAcquisitionUpload',
      'maxParityOvertimeTrim', 'maxParityOvertimeMetadata',
      'archiveMetadata', 'archiveOvertimeMetadata', 'archiveValidateFiles', 'archiveIrisUpload', 'archiveStartManualUpload', 'archiveUpload', 'archiveOvertimeUpload',
      'irisStartManualUpload', 'irisOvertimeStartManualUpload',
      'restripe',
      'c3Upload',
      'backupUpload',
      'collectS3Links',
      'finished'
    ];
    return tasks?.sort((a, b) => pattern.indexOf(a.key) === pattern.indexOf(b.key) ? 0 : (pattern.indexOf(a.key) > pattern.indexOf(b.key) ? 1 : -1)) || [];
  }

  static getTrimPoints(record: Record, serverId: string, isOvertime: boolean = false): {start?: string, end?: string} {
    const types: RecordTaskType[] = isOvertime ? ['maxParityOvertimeTrim'] : ['vodTrim'];
    return record.servers?.find(server => server.id === serverId)?.tasks.find(task => types.includes(task.type))?.body[isOvertime ? 'maxParityOvertime' : 'vod']?.trim || {};
  }

  static updateTrimPoints(recordsService: RecordsService, record: Record, serverId: string, trim: {start?: string, end?: string}, isOvertime: boolean = false): void {
    const types: RecordTaskType[] = isOvertime ? ['maxParityOvertimeTrim'] : ['vodTrim', 'maxParityTrim', 'maxTrim', 'linearTrim', 'contentAcquisitionTranscode', 'linearTranscode'];
    const stateType: {[key: string]: string} = {
      'vodTrim': 'vod',
      'maxTrim': 'max',
      'maxParityTrim': 'maxParity',
      'maxParityOvertimeTrim': 'maxParityOvertime',
      'linearTrim': 'linear',
      'linearTranscode': 'linear',
      'contentAcquisitionTranscode': 'contentAcquisition'
    };
    const tasks = record.servers?.find(server => server.id === serverId)?.tasks;
    tasks?.forEach(task => {
      if (types.includes(task.type)) {
        if (!task.body) {
          task.body = {};
        }
        const key = stateType[task.type];
        if (!task.body[key]) {
          task.body[key] = {}
        }
        if (!task.body[key].trim) {
          task.body[key].trim = {}
        }
        if (trim.start) {
          task.body[key].trim['start'] = trim.start;
        }
        if (trim.end) {
          task.body[key].trim['end'] = trim.end;
        }
        recordsService.updateTask(serverId, task);
      }
    });
  }

  static getICapPoints(record: Record, serverId: string): {start?: string, end?: string} {
    return record.servers?.find(server => server.id === serverId)?.tasks.find(task => task.type === 'startIcap')?.body?.icap || {};
  }

  static updateICapPoints(recordsService: RecordsService, record: Record, serverId: string, points: {start?: string, end?: string}): void {
    const tasks = record.servers?.find(server => server.id === serverId)?.tasks;
    const task = tasks?.find(task => task.type === 'startIcap');
    if (task) {
      if (!task.body) {
          task.body = {};
        }
        if (!task.body.icap) {
          task.body.icap = {}
        }
        if (points.start) {
          task.body.icap.start = points.start;
        }
        if (points.end) {
          task.body.icap.end = points.end;
        }
        recordsService.updateTask(serverId, task);
    }
  }

  static getFfmpegPoints(record: Record, serverId: string): {start?: string, end?: string} {
    const body = record.servers?.find(server => server.id === serverId)?.tasks.find(task => task.type === 'linearFfmpegTranscode')?.body?.linear || {};
    return {start: body?.inPointSeconds, end: body?.outPointSeconds};
  }

  static updateFfmpegPoints(recordsService: RecordsService, record: Record, serverId: string, points: {start?: string, end?: string}): void {
    const tasks = record.servers?.find(server => server.id === serverId)?.tasks;
    const task = tasks?.find(task => task.type === 'linearFfmpegTranscode');
    if (task) {
      if (!task.body) {
          task.body = {};
        }
        if (!task.body.linear) {
          task.body.linear = {}
        }
        if (points.start) {
          task.body.linear.inPointSeconds = points.start;
        }
        if (points.end) {
          task.body.linear.outPointSeconds = points.end;
        }
        recordsService.updateTask(serverId, task);
    }
  }

}